import React, { useState, useRef, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
	Stepper,
	Step,
	StepLabel,
	Button,
	LinearProgress,
	Chip,
	IconButton,
	ToggleButtonGroup,
	ToggleButton,
	TextField,
	Alert,
	Paper,
} from "@mui/material";
import { useSelector } from "react-redux";

import DropdownController from "../../components/inputs/DropdownController";
import TextFieldController from "../../components/inputs/TextFieldController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { maxLengthMessage } from "../../utils/functions";

// ── Constants
const STEPS = ["Vehicle Info", "Checklist", "Review & Submit"];

// ── Validation
const inspectionSchema = yup.object().shape({
	vehicleId: yup
		.number()
		.nullable()
		.required("Vehicle is required")
		.transform((v, o) => (o === "" ? null : v)),
	perPersonnelId: yup
		.number()
		.nullable()
		.required("Inspecting employee is required")
		.transform((v, o) => (o === "" ? null : v)),
	type: yup.string().required("Inspection type is required"),
	odometer: yup
		.number()
		.transform((v, o) => (o === "" ? null : v))
		.nullable()
		.required("Odometer reading is required"),
	notes: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),
});

const buildDefaults = (initialData) => ({
	vehicleId: initialData?.vehicleId || "",
	perPersonnelId: initialData?.perPersonnelId
		? String(initialData?.perPersonnelId)
		: "",
	type: initialData?.type || initialData?.typeInfo?.Code,
	odometer: initialData?.odometer || "",
	notes: initialData?.notes || "",
});

// ── Component
const AddEditInspectionDialog = ({
	open,
	onClose,
	onSave,
	initialData,
	vehicles = [],
	employees = [],
	checklistItems = [],
	inspectionTypeOptions = [],
}) => {
	const user = useSelector((state) => state.user);
	const isEdit = !!initialData;

	// ── Wizard step
	const [step, setStep] = useState(0);

	// ── Checklist responses (managed outside react-hook-form) ────────────────
	const [responses, setResponses] = useState({});

	// ── Photo state
	const [photos, setPhotos] = useState([]);
	const [showCameraModal, setShowCameraModal] = useState(false);
	const [cameraError, setCameraError] = useState("");

	// ── Refs ─
	const fileInputRef = useRef(null);
	const videoRef = useRef(null);
	const canvasRef = useRef(null);
	const streamRef = useRef(null);
	const scrollRef = useRef(null);
	const itemRefs = useRef({});

	// ── react-hook-form
	const {
		control,
		handleSubmit,
		reset,
		watch,
		trigger,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(inspectionSchema),
		defaultValues: buildDefaults(initialData),
		mode: "onChange",
	});

	const vehicleId = watch("vehicleId");
	const perPersonnelId = watch("perPersonnelId");
	const type = watch("type");
	const odometer = watch("odometer");
	const notes = watch("notes");

	// ── Derived data ───
	const selectedVehicle = useMemo(
		() => vehicles.find((v) => v.value === Number(vehicleId)),
		[vehicles, vehicleId],
	);

	const isOOS =
		selectedVehicle?.status === "Out of Service" ||
		selectedVehicle?.status === "Retired";

	const activeChecklist = useMemo(() => {
		return checklistItems.filter(
			(i) =>
				i.isActive !== false &&
				(!i.inspectionTypes ||
					i.inspectionTypes.length === 0 ||
					i.inspectionTypes.includes(type)),
		);
	}, [checklistItems, type]);

	const totalItems = activeChecklist.length;
	const answered = activeChecklist.filter(
		(i) => responses[i.id]?.status,
	).length;
	const pct =
		totalItems > 0 ? Math.round((answered / totalItems) * 100) : 100;

	const passCount = activeChecklist.filter(
		(i) => responses[i.id]?.status === "Pass",
	).length;
	const failCount = activeChecklist.filter(
		(i) => responses[i.id]?.status === "Fail",
	).length;
	const naCount = activeChecklist.filter(
		(i) => responses[i.id]?.status === "N/A",
	).length;

	const effectiveStatus =
		failCount > 0
			? "Fail"
			: answered === totalItems && totalItems > 0
				? "Pass"
				: "Needs Attention";

	const canGoNext =
		step === 0
			? !!(vehicleId && perPersonnelId && odometer && !isOOS)
			: step === 1
				? totalItems === 0 || answered === totalItems
				: true;

	// ── Reset everything when dialog opens / closes
	useEffect(() => {
		if (open) {
			reset(buildDefaults(initialData));
			setStep(0);

			// Load existing photos when editing
			if (initialData?.photos && initialData.photos.length > 0) {
				const loadedPhotos = initialData.photos.map((photo) => ({
					id: photo.id, // This is imageID from DB (numeric)
					src: photo.src,
					name: photo.name,
					size: photo.size || 0,
					notes: photo.notes || "",
				}));
				setPhotos(loadedPhotos);
			} else {
				setPhotos([]);
			}

			// Initialize responses
			const initial = {};
			if (initialData?.responses) {
				initialData.responses.forEach((r) => {
					initial[r.checklistItemId] = {
						status: r.status,
						comment: r.comment || "",
					};
				});
			}
			checklistItems
				.filter((i) => i.isActive !== false)
				.forEach((item) => {
					if (!initial[item.id]) {
						initial[item.id] = { status: "", comment: "" };
					}
				});
			setResponses(initial);
		}
	}, [open, checklistItems, reset, initialData]);

	useEffect(() => {
		if (!open) {
			reset();
			setStep(0);
			setPhotos([]);
			setResponses({});
			stopCameraStream();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, reset]);

	// Cleanup camera on unmount
	useEffect(() => {
		return () => {
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((t) => t.stop());
			}
		};
	}, []);

	const prevTypeRef = useRef(type);
	useEffect(() => {
		if (prevTypeRef.current !== type) {
			prevTypeRef.current = type;
			const newChecklist = checklistItems.filter(
				(i) =>
					i.isActive !== false &&
					(!i.inspectionTypes ||
						i.inspectionTypes.length === 0 ||
						i.inspectionTypes.includes(type)),
			);
			setResponses((prev) => {
				const next = {};
				newChecklist.forEach((item) => {
					next[item.id] = prev[item.id] || {
						status: "",
						comment: "",
					};
				});
				return next;
			});
		}
	}, [type, checklistItems]);

	// ── Helpers ────
	const setResponse = (checklistItemId, field, value) => {
		setResponses((r) => ({
			...r,
			[checklistItemId]: { ...r[checklistItemId], [field]: value },
		}));
	};

	const handleStatusChange = (checklistItemId, newValue) => {
		setResponse(checklistItemId, "status", newValue || "");
		requestAnimationFrame(() => {
			const container = scrollRef.current;
			const el = itemRefs.current[checklistItemId];
			if (container && el) {
				const itemTop = el.getBoundingClientRect().top;
				const containerTop = container.getBoundingClientRect().top;
				container.scrollTop += itemTop - containerTop;
			}
		});
	};

	// ── Mileage warning
	const mileageWarning = useMemo(() => {
		if (!odometer || !selectedVehicle?.odometer) return null;
		const diff = +odometer - selectedVehicle.odometer;
		if (diff < -100)
			return {
				severity: "warning",
				text: `Odometer is ${Math.abs(diff).toLocaleString()} mi less than current reading (${selectedVehicle.odometer.toLocaleString()} mi)`,
			};
		if (diff > 1000)
			return {
				severity: "info",
				text: `${diff.toLocaleString()} mi since last recorded reading`,
			};
		return null;
	}, [odometer, selectedVehicle]);

	// ── Step navigation
	const handleNext = async () => {
		if (step === 0) {
			const valid = await trigger();
			if (!valid || isOOS) return;
		}
		if (!canGoNext) return;
		setStep((s) => s + 1);
	};

	// ── Camera / Photos
	const startCameraStream = async () => {
		setCameraError("");
		setShowCameraModal(true);
		setTimeout(async () => {
			try {
				const stream = await navigator.mediaDevices.getUserMedia({
					video: { facingMode: "environment" },
				});
				streamRef.current = stream;
				if (videoRef.current) {
					videoRef.current.srcObject = stream;
					videoRef.current.play();
				}
			} catch {
				setCameraError("Camera unavailable or permission denied.");
			}
		}, 100);
	};

	const stopCameraStream = () => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((t) => t.stop());
			streamRef.current = null;
		}
		setShowCameraModal(false);
		setCameraError("");
	};

	const capturePhoto = () => {
		const video = videoRef.current;
		const canvas = canvasRef.current;
		if (!video || !canvas) return;
		canvas.width = video.videoWidth || 640;
		canvas.height = video.videoHeight || 480;
		canvas.getContext("2d").drawImage(video, 0, 0);
		const src = canvas.toDataURL("image/jpeg", 0.85);

		// Use string ID for new photos to differentiate from existing (numeric ID)
		setPhotos((prev) => [
			...prev,
			{
				id: `new-${Date.now()}-${Math.random()}`,
				src,
				name: `capture-${Date.now()}.jpg`,
				size: Math.round((src.length * 3) / 4), // Approximate size
			},
		]);
		stopCameraStream();
	};

	const handlePhotoFiles = (files) => {
		Array.from(files).forEach((file) => {
			if (!file.type.startsWith("image/")) return;
			const reader = new FileReader();
			reader.onload = (e) => {
				// Use string ID for new photos
				setPhotos((prev) => [
					...prev,
					{
						id: `new-${Date.now()}-${Math.random()}`,
						src: e.target.result,
						name: file.name,
						size: file.size,
					},
				]);
			};
			reader.readAsDataURL(file);
		});
	};

	const handleRemovePhoto = (photoId) => {
		setPhotos((prev) => prev.filter((p) => p.id !== photoId));
	};

	// ── Submit
	const onFormSubmit = (data) => {
		const failItems = activeChecklist.filter(
			(i) => responses[i.id]?.status === "Fail",
		);

		const payload = {
			id: initialData?.id || null,
			vehicleId: +data.vehicleId,
			perPersonnelId: +data.perPersonnelId,
			type: data.type,
			date: initialData?.date || new Date().toISOString(),
			odometer: +data.odometer,
			overallStatus: effectiveStatus,
			notes: data.notes?.trim() || null,
			// Photos array - includes both existing (numeric id) and new (string id)
			photos: photos.map((p) => ({
				id: p.id,
				src: p.src,
				name: p.name,
				size: p.size || 0,
				notes: p.notes || null,
			})),
			responses: activeChecklist.map((i) => ({
				checklistItemId: i.id,
				status: responses[i.id]?.status || "",
				comment: responses[i.id]?.comment || "",
			})),
			agencyId: user?.userAgency || null,
		};

		onSave(payload, failItems, +data.odometer);
	};

	// ── Option arrays ──
	const vehicleOpts = vehicles
		.filter((v) => v.status !== "Retired")
		.map((v) => ({
			value: v.value,
			label: `${v.label} [${v.status || "Unknown"}]`,
		}));

	const employeeOpts = employees.map((o) => ({
		value: o.value,
		label: o.label,
	}));

	const checklistCount = activeChecklist.length;

	// ── Dialog title ───
	const dialogTitle = isEdit
		? step === 0
			? `Edit Inspection — ${selectedVehicle?.unitNumber || ""}`
			: step === 1
				? `${type} Checklist — ${selectedVehicle?.unitNumber || ""}`
				: "Review & Submit"
		: step === 0
			? "New Vehicle Inspection"
			: step === 1
				? `${type} Checklist — ${selectedVehicle?.unitNumber || ""} ${selectedVehicle?.make || ""} ${selectedVehicle?.model || ""}`
				: "Review & Submit";

	// ── Render
	return (
		<>
			<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
				<DialogTitle>{dialogTitle}</DialogTitle>

				{/* Step Indicator */}
				<Box sx={{ px: 3, pb: 1, marginTop: "20px" }}>
					<Stepper activeStep={step} alternativeLabel>
						{STEPS.map((label) => (
							<Step key={label}>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>
				</Box>

				<DialogContent>
					<Box
						component="form"
						onSubmit={handleSubmit(onFormSubmit)}
						noValidate
					>
						{/* ── STEP 0: Vehicle Info ── */}
						{step === 0 && (
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(2, 1fr)",
									gap: 2,
								}}
							>
								<Box sx={{ gridColumn: "1 / -1" }}>
									<DropdownController
										name="vehicleId"
										label="Vehicle *"
										options={vehicleOpts}
										control={control}
										errors={errors}
										placeholder="— Select vehicle —"
										disabled={isEdit}
									/>
									{isEdit && (
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ mt: 0.5, display: "block" }}
										>
											Vehicle cannot be changed after
											creation
										</Typography>
									)}
									{isOOS && (
										<Alert severity="error" sx={{ mt: 1 }}>
											🚫 This vehicle is{" "}
											{selectedVehicle?.status}.
											Inspections cannot be performed on
											out-of-service or retired vehicles.
										</Alert>
									)}
								</Box>

								<Box sx={{ gridColumn: "1 / -1" }}>
									<DropdownController
										name="perPersonnelId"
										label="Inspecting Employee *"
										options={employeeOpts}
										control={control}
										errors={errors}
									/>
								</Box>

								<Box>
									<DropdownController
										name="type"
										label="Inspection Type *"
										options={inspectionTypeOptions.map(
											(t) => ({
												value: t.value || t,
												label: t.label || t,
											}),
										)}
										control={control}
										errors={errors}
									/>
									{type && (
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{
												mt: 0.5,
												display: "block",
											}}
										>
											📋 <strong>{checklistCount}</strong>{" "}
											checklist item
											{checklistCount !== 1
												? "s"
												: ""}{" "}
											for this inspection type
										</Typography>
									)}
								</Box>

								<Box>
									<TextFieldController
										name="odometer"
										label="Odometer Reading (mi) *"
										control={control}
										errors={errors}
										type="number"
										placeholder={
											selectedVehicle?.odometer
												? String(
														selectedVehicle.odometer,
													)
												: ""
										}
									/>
									{mileageWarning && (
										<Alert
											severity={mileageWarning.severity}
											sx={{ mt: 1 }}
										>
											{mileageWarning.text}
										</Alert>
									)}
								</Box>

								<Box sx={{ gridColumn: "1 / -1" }}>
									<TextFieldController
										name="notes"
										label="General Notes"
										control={control}
										errors={errors}
										multiline
										rows={3}
									/>
								</Box>
							</Box>
						)}

						{/* ── STEP 1: Checklist ── */}
						{step === 1 && (
							<Box>
								{activeChecklist.length === 0 ? (
									<Box sx={{ textAlign: "center", py: 5 }}>
										<Typography variant="h4" sx={{ mb: 1 }}>
											☑️
										</Typography>
										<Typography
											variant="body2"
											color="text.secondary"
										>
											No checklist items for{" "}
											<strong>{type}</strong> inspections
										</Typography>
										<Typography
											variant="caption"
											color="text.disabled"
											sx={{
												mt: 1,
												display: "block",
											}}
										>
											Add items in Checklist Manager and
											assign them to this inspection type.
										</Typography>
									</Box>
								) : (
									<>
										{/* Progress */}
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 2,
												mb: 2,
											}}
										>
											<LinearProgress
												variant="determinate"
												value={pct}
												sx={{
													flex: 1,
													height: 8,
													borderRadius: 4,
												}}
												color={
													pct === 100
														? "success"
														: "primary"
												}
											/>
											<Typography
												variant="caption"
												fontWeight={600}
												color="text.secondary"
												sx={{ whiteSpace: "nowrap" }}
											>
												{answered}/{totalItems}{" "}
												completed
											</Typography>
										</Box>

										{/* Checklist Items */}
										<Box
											ref={scrollRef}
											sx={{
												maxHeight: 420,
												overflowY: "auto",
												display: "flex",
												flexDirection: "column",
												gap: 1,
											}}
										>
											{activeChecklist.map((item) => {
												const resp = responses[
													item.id
												] || {
													status: "",
													comment: "",
												};
												const borderColor =
													resp.status === "Pass"
														? "success.main"
														: resp.status === "Fail"
															? "error.main"
															: resp.status ===
																  "N/A"
																? "grey.400"
																: "divider";

												return (
													<Paper
														key={item.id}
														ref={(el) =>
															(itemRefs.current[
																item.id
															] = el)
														}
														variant="outlined"
														sx={{
															p: 2,
															borderColor,
															borderLeftWidth: 3,
															display: "flex",
															justifyContent:
																"space-between",
															alignItems:
																"flex-start",
															gap: 2,
														}}
													>
														<Box sx={{ flex: 1 }}>
															<Typography
																variant="body2"
																fontWeight={600}
															>
																{item.name}
															</Typography>
															{item.description && (
																<Typography
																	variant="caption"
																	color="text.secondary"
																>
																	{
																		item.description
																	}
																</Typography>
															)}
															{resp.status && (
																<TextField
																	size="small"
																	fullWidth
																	rows={2}
																	label={
																		resp.status ===
																		"Fail"
																			? "Describe the issue (required for Fail)…"
																			: "Optional comment…"
																	}
																	value={
																		resp.comment
																	}
																	onChange={(
																		e,
																	) =>
																		setResponse(
																			item.id,
																			"comment",
																			e
																				.target
																				.value,
																		)
																	}
																	sx={{
																		mt: 1,
																	}}
																/>
															)}
														</Box>

														<ToggleButtonGroup
															size="small"
															exclusive
															value={
																resp.status ||
																null
															}
															onChange={(
																_,
																val,
															) =>
																handleStatusChange(
																	item.id,
																	val,
																)
															}
														>
															<ToggleButton
																value="Pass"
																sx={{
																	"&.Mui-selected":
																		{
																			bgcolor:
																				"success.light",
																			color: "success.dark",
																			"&:hover":
																				{
																					bgcolor:
																						"success.light",
																				},
																		},
																}}
															>
																Pass
															</ToggleButton>
															<ToggleButton
																value="Fail"
																sx={{
																	"&.Mui-selected":
																		{
																			bgcolor:
																				"error.light",
																			color: "error.dark",
																			"&:hover":
																				{
																					bgcolor:
																						"error.light",
																				},
																		},
																}}
															>
																Fail
															</ToggleButton>
															<ToggleButton
																value="N/A"
																sx={{
																	"&.Mui-selected":
																		{
																			bgcolor:
																				"grey.300",
																			color: "grey.700",
																			"&:hover":
																				{
																					bgcolor:
																						"grey.300",
																				},
																		},
																}}
															>
																N/A
															</ToggleButton>
														</ToggleButtonGroup>
													</Paper>
												);
											})}
										</Box>
									</>
								)}
							</Box>
						)}

						{/* ── STEP 2: Review & Submit ── */}
						{step === 2 && (
							<Box>
								{/* Result Banner */}
								<Alert
									severity={
										effectiveStatus === "Pass"
											? "success"
											: effectiveStatus === "Fail"
												? "error"
												: "warning"
									}
									sx={{ mb: 2 }}
								>
									<Typography variant="subtitle2">
										Overall Result: {effectiveStatus}
									</Typography>
									<Typography variant="caption">
										{failCount > 0
											? `${failCount} failed item${failCount > 1 ? "s" : ""} detected — work order recommended`
											: "All checked items passed"}
									</Typography>
								</Alert>

								{/* Summary Cards */}
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: "repeat(3, 1fr)",
										gap: 2,
										mb: 2,
									}}
								>
									<Paper
										variant="outlined"
										sx={{ p: 2, textAlign: "center" }}
									>
										<Typography
											variant="h5"
											fontWeight={700}
											color="success.main"
										>
											{passCount}
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											Passed
										</Typography>
									</Paper>
									<Paper
										variant="outlined"
										sx={{ p: 2, textAlign: "center" }}
									>
										<Typography
											variant="h5"
											fontWeight={700}
											color="error.main"
										>
											{failCount}
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											Failed
										</Typography>
									</Paper>
									<Paper
										variant="outlined"
										sx={{ p: 2, textAlign: "center" }}
									>
										<Typography
											variant="h5"
											fontWeight={700}
											color="text.disabled"
										>
											{naCount}
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											N/A
										</Typography>
									</Paper>
								</Box>

								{/* Vehicle / Inspection Info */}
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mb: 2 }}
								>
									<strong>Vehicle:</strong>{" "}
									{selectedVehicle?.unitNumber ||
										selectedVehicle?.label}{" "}
									— {selectedVehicle?.year}{" "}
									{selectedVehicle?.make}{" "}
									{selectedVehicle?.model}
									&nbsp;&nbsp;|&nbsp;&nbsp;
									<strong>Odometer:</strong>{" "}
									{(+odometer).toLocaleString()} mi
									&nbsp;&nbsp;|&nbsp;&nbsp;
									<strong>Type:</strong> {type}
								</Typography>

								{/* Failed Items */}
								{failCount > 0 && (
									<Box sx={{ mb: 2 }}>
										<Typography
											variant="caption"
											fontWeight={700}
											color="error.main"
											sx={{
												textTransform: "uppercase",
												letterSpacing: "0.06em",
												mb: 1,
												display: "block",
											}}
										>
											Failed Items
										</Typography>
										<Box
											sx={{
												maxHeight: 180,
												overflowY: "auto",
											}}
										>
											{activeChecklist
												.filter(
													(i) =>
														responses[i.id]
															?.status === "Fail",
												)
												.map((item) => (
													<Paper
														key={item.id}
														variant="outlined"
														sx={{
															p: "10px 14px",
															mb: 1,
															borderColor:
																"error.light",
															display: "flex",
															justifyContent:
																"space-between",
															alignItems:
																"center",
														}}
													>
														<Box>
															<Typography
																variant="body2"
																fontWeight={600}
															>
																{item.name}
															</Typography>
															{responses[item.id]
																?.comment && (
																<Typography
																	variant="caption"
																	color="text.secondary"
																	fontStyle="italic"
																>
																	"
																	{
																		responses[
																			item
																				.id
																		]
																			?.comment
																	}
																	"
																</Typography>
															)}
														</Box>
														<Chip
															label="Fail"
															color="error"
															size="small"
														/>
													</Paper>
												))}
										</Box>
									</Box>
								)}

								{/* Notes */}
								{notes && (
									<Paper
										variant="outlined"
										sx={{ p: "10px 12px", mb: 2 }}
									>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											<strong>Notes:</strong> {notes}
										</Typography>
									</Paper>
								)}

								{/* Photos */}
								<Box sx={{ mt: 2 }}>
									<Typography
										variant="caption"
										fontWeight={700}
										color="text.secondary"
										sx={{
											textTransform: "uppercase",
											letterSpacing: "0.06em",
											mb: 1,
											display: "block",
										}}
									>
										Photos ({photos.length})
										<span
											style={{
												fontWeight: 400,
												textTransform: "none",
												marginLeft: 4,
											}}
										>
											— optional
										</span>
									</Typography>

									{/* Hidden file input & canvas */}
									<input
										ref={fileInputRef}
										type="file"
										accept="image/*"
										multiple
										style={{ display: "none" }}
										onChange={(e) => {
											handlePhotoFiles(e.target.files);
											e.target.value = "";
										}}
									/>
									<canvas
										ref={canvasRef}
										style={{ display: "none" }}
									/>

									<Box
										sx={{
											display: "flex",
											gap: 1,
											mb: photos.length > 0 ? 2 : 0,
										}}
									>
										<Button
											variant="outlined"
											size="small"
											onClick={startCameraStream}
										>
											📷 Camera
										</Button>
										<Button
											variant="outlined"
											size="small"
											onClick={() =>
												fileInputRef.current?.click()
											}
										>
											📎 Attach File
										</Button>
									</Box>

									{/* Photo Grid */}
									{photos.length > 0 && (
										<Box
											sx={{
												display: "grid",
												gridTemplateColumns:
													"repeat(auto-fill, minmax(90px, 1fr))",
												gap: 1,
											}}
										>
											{photos.map((p) => (
												<Box
													key={p.id}
													sx={{
														position: "relative",
														aspectRatio: "1",
														borderRadius: 1,
														overflow: "hidden",
														border: "1px solid",
														borderColor: "divider",
													}}
												>
													<img
														src={p.src}
														alt={p.name}
														style={{
															width: "100%",
															height: "100%",
															objectFit: "cover",
														}}
													/>
													<IconButton
														size="small"
														onClick={() =>
															handleRemovePhoto(
																p.id,
															)
														}
														sx={{
															position:
																"absolute",
															top: 2,
															right: 2,
															bgcolor:
																"rgba(0,0,0,0.6)",
															color: "#fff",
															width: 22,
															height: 22,
															"&:hover": {
																bgcolor:
																	"rgba(0,0,0,0.8)",
															},
														}}
													>
														✕
													</IconButton>
												</Box>
											))}
										</Box>
									)}
								</Box>
							</Box>
						)}
					</Box>
				</DialogContent>

				<DialogActions sx={{ px: 3, pb: 2 }}>
					{step > 0 && (
						<Button onClick={() => setStep((s) => s - 1)}>
							← Back
						</Button>
					)}
					<PATCloseButton onClick={onClose} tooltipText="Cancel" />
					{step < 2 ? (
						<Button
							variant="contained"
							disabled={!canGoNext}
							onClick={handleNext}
						>
							Next →
						</Button>
					) : (
						<PATSaveButton
							onClick={handleSubmit(onFormSubmit)}
							tooltipText={
								isEdit
									? "Update Inspection"
									: "Submit Inspection"
							}
						/>
					)}
				</DialogActions>
			</Dialog>

			{/* ── Camera Capture Dialog ── */}
			<Dialog
				open={showCameraModal}
				onClose={stopCameraStream}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>📷 Take Photo</DialogTitle>
				<DialogContent sx={{ p: 0, overflow: "hidden" }}>
					{cameraError ? (
						<Box sx={{ p: 4, textAlign: "center" }}>
							<Typography variant="body2" color="warning.main">
								{cameraError}
							</Typography>
						</Box>
					) : (
						<Box sx={{ position: "relative", bgcolor: "#000" }}>
							<video
								ref={videoRef}
								style={{
									width: "100%",
									display: "block",
									maxHeight: 360,
									objectFit: "cover",
								}}
								muted
								playsInline
							/>
							<Box
								sx={{
									position: "absolute",
									inset: 0,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									pointerEvents: "none",
								}}
							>
								<Box
									sx={{
										width: "60%",
										height: "60%",
										border: "2px solid rgba(255,255,255,0.4)",
										borderRadius: 1,
									}}
								/>
							</Box>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<PATCloseButton
						onClick={stopCameraStream}
						tooltipText="Cancel"
					/>
					{!cameraError && (
						<Button variant="contained" onClick={capturePhoto}>
							📸 Capture
						</Button>
					)}
				</DialogActions>
			</Dialog>
		</>
	);
};

export default AddEditInspectionDialog;
